import { Injectable } from '@angular/core';
import { fromEvent, merge, BehaviorSubject, of, timer } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';

/**
 * Tempo que o navegador precisa reportar "offline" de forma contínua antes de
 * tratarmos como uma queda real de internet. Durante o cold start (sobretudo no
 * webview nativo), o `navigator.onLine` pode reportar `false` por alguns
 * instantes enquanto os chunks ainda estão carregando, mesmo com a internet
 * funcionando. Só consideramos offline de verdade se o estado persistir por
 * esse período — assim o aviso de "sem conexão" não pisca durante o load.
 * Voltar a ficar online é sempre imediato (não passa por esse atraso).
 */
const OFFLINE_CONFIRM_DELAY_MS = 2500;

@Injectable({ providedIn: 'root' })
export class NetworkStatusService {
  private readonly _isOnline$ = new BehaviorSubject<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  /**
   * `true` assim que a conexão volta; `false` apenas depois de
   * OFFLINE_CONFIRM_DELAY_MS de offline contínuo. Se a conexão voltar antes
   * desse intervalo (ex.: blip transitório no carregamento inicial), o offline
   * é descartado e nada é emitido.
   */
  readonly isOnline$ = this._isOnline$.pipe(
    switchMap(online =>
      online ? of(true) : timer(OFFLINE_CONFIRM_DELAY_MS).pipe(map(() => false))
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  get isOnline(): boolean { return this._isOnline$.getValue(); }

  private readonly _isSlowConnection$ = new BehaviorSubject<boolean>(false);
  readonly isSlowConnection$ = this._isSlowConnection$.asObservable();

  constructor() {
    if (typeof window === 'undefined') return;

    merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false)),
    ).subscribe(online => this._isOnline$.next(online));

    const conn = (navigator as any).connection
      || (navigator as any).mozConnection
      || (navigator as any).webkitConnection;

    if (conn) {
      this._isSlowConnection$.next(this.isConnSlow(conn));
      fromEvent(conn, 'change').subscribe(() =>
        this._isSlowConnection$.next(this.isConnSlow(conn))
      );
    }
  }

  private isConnSlow(conn: any): boolean {
    if (['slow-2g', '2g'].includes(conn.effectiveType)) return true;
    if (conn.rtt != null && conn.rtt > 500) return true;
    if (conn.downlink != null && conn.downlink > 0 && conn.downlink < 1) return true;
    return false;
  }
}
